/**
 * GlobalView — Entity Store
 *
 * Zustand store for managing live entity data from WebSocket.
 * Maintains a Map<id, Entity> for fast lookups and updates.
 */

import { create } from 'zustand';
import type { Entity, EntityType } from '@globalview/shared';

interface EntityState {
  /** All live entities indexed by ID */
  entities: Map<string, Entity>;

  /** Whether the WebSocket is connected */
  wsConnected: boolean;

  /** Source health status */
  sourceStatus: 'online' | 'degraded' | 'offline' | 'unknown';

  /** Last update timestamp */
  lastUpdate: string | null;

  // ─── Actions ──────────────────────────────────────────────────

  /** Upsert entities (add or update) */
  upsertEntities: (entities: Entity[]) => void;

  /** Remove entities by ID */
  removeEntities: (ids: string[]) => void;

  /** Clear all entities of a given type */
  clearByType: (type: EntityType) => void;

  /** Clear all entities */
  clearAll: () => void;

  /** Set WebSocket connection status */
  setWsConnected: (connected: boolean) => void;

  /** Set source health */
  setSourceStatus: (status: 'online' | 'degraded' | 'offline' | 'unknown') => void;
}

export const useEntityStore = create<EntityState>((set) => ({
  entities: new Map(),
  wsConnected: false,
  sourceStatus: 'unknown',
  lastUpdate: null,

  upsertEntities: (incoming) =>
    set((state) => {
      const next = new Map(state.entities);
      for (const entity of incoming) {
        next.set(entity.id, entity);
      }
      return { entities: next, lastUpdate: new Date().toISOString() };
    }),

  removeEntities: (ids) =>
    set((state) => {
      const next = new Map(state.entities);
      for (const id of ids) {
        next.delete(id);
      }
      return { entities: next };
    }),

  clearByType: (type) =>
    set((state) => {
      const next = new Map(state.entities);
      for (const [id, entity] of next) {
        if (entity.type === type) next.delete(id);
      }
      return { entities: next };
    }),

  clearAll: () => set({ entities: new Map(), lastUpdate: null }),

  setWsConnected: (connected) => set({ wsConnected: connected }),

  setSourceStatus: (status) => set({ sourceStatus: status }),
}));

// ─── Selectors ───────────────────────────────────────────────────

/** Get all aircraft entities */
export function selectAircraft(state: EntityState): Entity[] {
  const result: Entity[] = [];
  for (const entity of state.entities.values()) {
    if (entity.type === 'aircraft') result.push(entity);
  }
  return result;
}

/** Get entity by ID */
export function selectEntityById(state: EntityState, id: string): Entity | undefined {
  return state.entities.get(id);
}

/** Get total entity count */
export function selectEntityCount(state: EntityState): number {
  return state.entities.size;
}

/** Get aircraft count */
export function selectAircraftCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'aircraft') count++;
  }
  return count;
}

/** Get all train entities */
export function selectTrains(state: EntityState): Entity[] {
  const result: Entity[] = [];
  for (const entity of state.entities.values()) {
    if (entity.type === 'train') result.push(entity);
  }
  return result;
}

/** Get train count */
export function selectTrainCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'train') count++;
  }
  return count;
}

/** Get all satellite entities */
export function selectSatellites(state: EntityState): Entity[] {
  const result: Entity[] = [];
  for (const entity of state.entities.values()) {
    if (entity.type === 'satellite') result.push(entity);
  }
  return result;
}

/** Get satellite count */
export function selectSatelliteCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'satellite') count++;
  }
  return count;
}

/** Get all jamming cell entities */
export function selectJammingCells(state: EntityState): Entity[] {
  const result: Entity[] = [];
  for (const entity of state.entities.values()) {
    if (entity.type === 'jamming_cell') result.push(entity);
  }
  return result;
}

/** Get jamming cell count */
export function selectJammingCellCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'jamming_cell') count++;
  }
  return count;
}

/** Get all zone entities */
export function selectZones(state: EntityState): Entity[] {
  const result: Entity[] = [];
  for (const entity of state.entities.values()) {
    if (entity.type === 'zone') result.push(entity);
  }
  return result;
}

/** Get zone count */
export function selectZoneCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'zone') count++;
  }
  return count;
}

/** Get all ship entities */
export function selectShips(state: EntityState): Entity[] {
  const result: Entity[] = [];
  for (const entity of state.entities.values()) {
    if (entity.type === 'ship') result.push(entity);
  }
  return result;
}

/** Get ship count */
export function selectShipCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'ship') count++;
  }
  return count;
}

/** Get all earthquake entities */
export function selectEarthquakes(state: EntityState): Entity[] {
  const result: Entity[] = [];
  for (const entity of state.entities.values()) {
    if (entity.type === 'earthquake') result.push(entity);
  }
  return result;
}

/** Get earthquake count */
export function selectEarthquakeCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'earthquake') count++;
  }
  return count;
}

/** Get global earthquake count (USGS source) */
export function selectGlobalEarthquakeCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'earthquake' && entity.source === 'usgs') count++;
  }
  return count;
}

/** Get Swiss earthquake count (SED source) */
export function selectSwissEarthquakeCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'earthquake' && entity.source === 'sed') count++;
  }
  return count;
}

/** Get all wildfire entities */
export function selectWildfires(state: EntityState): Entity[] {
  const result: Entity[] = [];
  for (const entity of state.entities.values()) {
    if (entity.type === 'wildfire') result.push(entity);
  }
  return result;
}

/** Get wildfire count */
export function selectWildfireCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'wildfire') count++;
  }
  return count;
}

/** Get all natural event entities */
export function selectNaturalEvents(state: EntityState): Entity[] {
  const result: Entity[] = [];
  for (const entity of state.entities.values()) {
    if (entity.type === 'natural_event') result.push(entity);
  }
  return result;
}

/** Get natural event count */
export function selectNaturalEventCount(state: EntityState): number {
  let count = 0;
  for (const entity of state.entities.values()) {
    if (entity.type === 'natural_event') count++;
  }
  return count;
}

/** Get natural event counts grouped by category */
export function selectNaturalEventCategoryCounts(
  state: EntityState,
): Map<string, { count: number; title: string }> {
  const result = new Map<string, { count: number; title: string }>();
  for (const entity of state.entities.values()) {
    if (entity.type !== 'natural_event') continue;
    const props = entity.properties as Record<string, unknown>;
    const categoryId = (props.categoryId as string) ?? 'unknown';
    const categoryTitle = (props.categoryTitle as string) ?? 'Unknown';
    const existing = result.get(categoryId);
    if (existing) {
      existing.count++;
    } else {
      result.set(categoryId, { count: 1, title: categoryTitle });
    }
  }
  return result;
}
